// Ko'rinish (Q-031): mavzu, asosiy rang, zichlik va yozuv kattaligi.
//
// Mavzu ranglari tokens.css'da ([data-theme='…']). Asosiy rang — shu yerda (8 ta tekshirilgan rang;
// har birining yorug' va tungi varianti) va <html> ga CSS o'zgaruvchilari bilan qo'yiladi.
// Ma'noli ranglar (qizil "Tugadi", yashil "Online", sariq ogohlantirish) hech qachon o'zgarmaydi.
//
// Kim nimani tanlaydi:
//   restoran (ega) — standart mavzu va asosiy rang (sozlama ui.theme, ui.accent);
//   har bir xodim — o'zi uchun mavzu (shu qurilmada), panelda yana zichlik va yozuv kattaligi.
import { useEffect } from 'react';

export type ThemeName = 'modern' | 'dark' | 'classic' | 'contrast';
/** Restoran standarti: aniq mavzu yoki "telefonga moslash" */
export type ThemeChoice = 'system' | ThemeName;
/** Xodimning o'z tanlovi: "auto" — restoran standarti */
export type PersonalTheme = 'auto' | ThemeChoice;
export type AccentName = 'amber' | 'blue' | 'green' | 'teal' | 'violet' | 'pink' | 'coffee' | 'graphite';
export type Density = 'normal' | 'compact';
export type TextSize = 's' | 'm' | 'l';

export const THEMES: readonly { value: ThemeName; label: string; hint: string }[] = [
  { value: 'modern', label: 'Zamonaviy', hint: 'Yorug‘, yumshoq burchaklar' },
  { value: 'dark', label: 'Tungi', hint: 'Qorong‘i fon, kechqurun ko‘zni charchatmaydi' },
  { value: 'classic', label: 'Klassik', hint: '1C, MoySklad uslubida: ixcham, aniq chegaralar' },
  { value: 'contrast', label: 'Yuqori kontrast', hint: 'Oshxona va yorug‘ joy uchun: oq fon, qora yozuv' },
];

export const THEME_LABEL: Record<PersonalTheme, string> = {
  auto: 'Restoran standarti',
  system: 'Telefonga moslash',
  modern: 'Zamonaviy',
  dark: 'Tungi',
  classic: 'Klassik',
  contrast: 'Yuqori kontrast',
};

export interface AccentVars {
  /** Tugma foni */
  accent: string;
  hover: string;
  /** Tanlangan joyning yumshoq foni */
  soft: string;
  /** Tugma ustidagi yozuv */
  ink: string;
  /** Oddiy fondagi rangli yozuv */
  text: string;
}

export const ACCENTS: Record<AccentName, { label: string; light: AccentVars; dark: AccentVars }> = {
  amber: {
    // ONE POS zafaroni (Q-032) — standart rang
    label: 'Zafaron',
    light: { accent: '#dd9436', hover: '#e9a54f', soft: '#fbefdd', ink: '#1c1303', text: '#975a08' },
    dark: { accent: '#e6a04a', hover: '#f0b263', soft: '#33260f', ink: '#1c1303', text: '#f0b263' },
  },
  blue: {
    label: 'Ko‘k',
    light: { accent: '#2563eb', hover: '#1d4fd8', soft: '#e6eefc', ink: '#ffffff', text: '#1d4ed8' },
    dark: { accent: '#5b93ff', hover: '#79a7ff', soft: '#16264a', ink: '#06122a', text: '#93b8ff' },
  },
  green: {
    label: 'Yashil',
    light: { accent: '#15803d', hover: '#166534', soft: '#e3f6ea', ink: '#ffffff', text: '#166534' },
    dark: { accent: '#34c46d', hover: '#4fd683', soft: '#10301f', ink: '#04170b', text: '#5ee39a' },
  },
  teal: {
    label: 'Firuza',
    light: { accent: '#0f766e', hover: '#115e59', soft: '#dff3f1', ink: '#ffffff', text: '#0f766e' },
    dark: { accent: '#2dd4bf', hover: '#5eead4', soft: '#0f2f2c', ink: '#03201c', text: '#5eead4' },
  },
  violet: {
    label: 'Binafsha',
    light: { accent: '#7c3aed', hover: '#6d28d9', soft: '#efe7fd', ink: '#ffffff', text: '#6d28d9' },
    dark: { accent: '#a78bfa', hover: '#c4b5fd', soft: '#271a45', ink: '#150a2e', text: '#c4b5fd' },
  },
  pink: {
    label: 'Pushti',
    light: { accent: '#db2777', hover: '#be185d', soft: '#fce7f1', ink: '#ffffff', text: '#be185d' },
    dark: { accent: '#f472b6', hover: '#f9a8d4', soft: '#3d1529', ink: '#2a0617', text: '#f9a8d4' },
  },
  coffee: {
    label: 'Qahvarang',
    light: { accent: '#8b5a2b', hover: '#73491f', soft: '#f3e9df', ink: '#ffffff', text: '#7a4d22' },
    dark: { accent: '#c8935f', hover: '#d9a978', soft: '#33251a', ink: '#1d1208', text: '#e0b386' },
  },
  graphite: {
    label: 'Grafit',
    light: { accent: '#374151', hover: '#1f2937', soft: '#eceef2', ink: '#ffffff', text: '#374151' },
    dark: { accent: '#cbd5e1', hover: '#e2e8f0', soft: '#253042', ink: '#0b1220', text: '#cbd5e1' },
  },
};

export const ACCENT_NAMES = Object.keys(ACCENTS) as AccentName[];
const THEME_NAMES: readonly string[] = ['modern', 'dark', 'classic', 'contrast'];

export function isThemeName(v: unknown): v is ThemeName {
  return typeof v === 'string' && THEME_NAMES.includes(v);
}

export function isAccent(v: unknown): v is AccentName {
  return typeof v === 'string' && v in ACCENTS;
}

export function isPersonalTheme(v: unknown): v is PersonalTheme {
  return v === 'auto' || v === 'system' || isThemeName(v);
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Qaysi mavzu ko'rinadi: xodimning tanlovi → restoran standarti → telefon */
export function resolveTheme(personal: PersonalTheme | undefined, restaurant: unknown, dark = systemPrefersDark()): ThemeName {
  const pick: ThemeChoice =
    personal && personal !== 'auto' ? personal : restaurant === 'system' || isThemeName(restaurant) ? (restaurant as ThemeChoice) : 'system';
  return pick === 'system' ? (dark ? 'dark' : 'modern') : pick;
}

/** Asosiy rang o'zgaruvchilari (tungi mavzuda — tungi varianti) */
export function accentVars(accent: unknown, theme: ThemeName): Record<string, string> {
  const set = ACCENTS[isAccent(accent) ? accent : 'amber'];
  const a = set[theme === 'dark' ? 'dark' : 'light'];
  return {
    // Yon panel (va bosh sahifadagi katta raqam) fonida: klassikda yon panel yorug', qolganlarida qorong'i
    '--op-side-accent': theme === 'classic' ? set.light.text : set.dark.accent,
    '--op-accent': a.accent,
    '--op-accent-hover': a.hover,
    '--op-accent-soft': a.soft,
    '--op-accent-ink': a.ink,
    '--op-accent-text': a.text,
  };
}

export interface UiState {
  theme: ThemeName;
  accent: AccentName;
  density?: Density;
  text?: TextSize;
}

/** Ko'rinishni butun sahifaga qo'llaydi */
export function applyUi(ui: UiState): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = ui.theme;
  root.dataset.density = ui.density ?? 'normal';
  root.dataset.text = ui.text ?? 'm';
  for (const [k, v] of Object.entries(accentVars(ui.accent, ui.theme))) root.style.setProperty(k, v);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', ui.theme === 'dark' || ui.theme === 'contrast' ? '#000000' : '#0e1626');
}

export interface UiInput {
  personal?: PersonalTheme;
  restaurant?: unknown;
  accent?: unknown;
  density?: Density;
  text?: TextSize;
}

/** React: ko'rinishni qo'llaydi va "telefonga moslash"da telefon o'zgarishini kuzatadi. Qo'llangan mavzuni qaytaradi. */
export function useApplyUi(input: UiInput): ThemeName {
  const { personal, restaurant, accent, density, text } = input;
  const theme = resolveTheme(personal, restaurant);
  useEffect(() => {
    const run = () =>
      applyUi({ theme: resolveTheme(personal, restaurant), accent: isAccent(accent) ? accent : 'amber', density, text });
    run();
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', run);
    return () => mq.removeEventListener('change', run);
  }, [personal, restaurant, accent, density, text]);
  return theme;
}

// ---------------------------------------------------------------------------
// Platforma konsoli: admin o'zi uchun (brauzerda saqlanadi)
// ---------------------------------------------------------------------------
const KEY = 'onepos.theme';

export function getThemeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light') return 'modern';
    if (v === 'system' || isThemeName(v)) return v as ThemeChoice;
  } catch {
    /* saqlash imkoni yo'q */
  }
  return 'system';
}

export function applyTheme(choice: ThemeChoice = getThemeChoice()): void {
  applyUi({ theme: resolveTheme(choice, 'system'), accent: 'amber' });
}

export function setThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(KEY, choice);
  } catch {
    /* saqlash imkoni yo'q — faqat joriy sahifaga qo'llanadi */
  }
  applyTheme(choice);
}

/** Ilova ochilganda bir marta: saqlangan tanlovni qo'llaydi va telefon o'zgarishini kuzatadi. */
export function initTheme(): void {
  applyTheme();
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getThemeChoice() === 'system') applyTheme('system');
  });
}
